/**
 * Generate a cryptographically random code verifier for PKCE.
 */
export function generateCodeVerifier(): string {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return base64UrlEncode(array);
}

/**
 * Generate a code challenge from a code verifier using SHA-256.
 */
export async function generateCodeChallenge(verifier: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return base64UrlEncode(new Uint8Array(digest));
}

/**
 * Base64 URL encode a Uint8Array.
 */
function base64UrlEncode(array: Uint8Array): string {
  // Use Array.from instead of spread to avoid stack overflow with large arrays
  const binary = Array.from(array, byte => String.fromCharCode(byte)).join('');
  const base64 = btoa(binary);
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/**
 * Generate a random state parameter.
 */
export function generateState(): string {
  const array = new Uint8Array(16);
  crypto.getRandomValues(array);
  return base64UrlEncode(array);
}

// Session storage keys for OAuth flow
const PKCE_VERIFIER_KEY = 'oauth_pkce_verifier';
const OAUTH_STATE_KEY = 'oauth_state';
const REDIRECT_PATH_KEY = 'oauth_redirect_path';

/**
 * Store PKCE verifier in session storage.
 */
export function storePkceVerifier(verifier: string): void {
  sessionStorage.setItem(PKCE_VERIFIER_KEY, verifier);
}

/**
 * Get and clear PKCE verifier from session storage.
 */
export function getAndClearPkceVerifier(): string | null {
  const verifier = sessionStorage.getItem(PKCE_VERIFIER_KEY);
  sessionStorage.removeItem(PKCE_VERIFIER_KEY);
  return verifier;
}

/**
 * Store OAuth state in session storage.
 */
export function storeOAuthState(state: string): void {
  sessionStorage.setItem(OAUTH_STATE_KEY, state);
}

/**
 * Get and clear OAuth state from session storage.
 */
export function getAndClearOAuthState(): string | null {
  const state = sessionStorage.getItem(OAUTH_STATE_KEY);
  sessionStorage.removeItem(OAUTH_STATE_KEY);
  return state;
}

/**
 * Store the current path to redirect back to after login.
 */
export function storeRedirectPath(path: string): void {
  sessionStorage.setItem(REDIRECT_PATH_KEY, path);
}

/**
 * Get and clear the redirect path.
 */
export function getAndClearRedirectPath(): string | null {
  const path = sessionStorage.getItem(REDIRECT_PATH_KEY);
  sessionStorage.removeItem(REDIRECT_PATH_KEY);
  return path;
}
